import { useState } from 'react';
import { Button, Input, InputNumber, Switch, Typography, Space, Divider, ColorPicker, Select, Collapse, Modal } from 'antd';
import { PlusOutlined, DeleteOutlined, BulbOutlined, SunOutlined, AimOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import useSceneStore from '../../stores/sceneStore';
import { useSelectionStore } from '../../stores/selectionStore';

const { Text } = Typography;

interface SceneLight {
  id: string;
  name: string;
  type: 'ambient' | 'directional' | 'point' | 'spot';
  enabled: boolean;
  color: string;
  intensity: number;
  position?: [number, number, number];
  target?: [number, number, number];
  distance?: number;
  decay?: number;
  angle?: number;
  penumbra?: number;
  castShadow?: boolean;
}

const lightTypeLabels: Record<string, string> = {
  ambient: '环境光',
  directional: '平行光',
  point: '点光源',
  spot: '聚光灯',
};

const lightTypeIcons: Record<string, React.ReactNode> = {
  ambient: <BulbOutlined />,
  directional: <SunOutlined />,
  point: <BulbOutlined />,
  spot: <AimOutlined />,
};

const defaultLightConfigs: Record<string, Partial<SceneLight>> = {
  ambient: { color: '#ffffff', intensity: 0.5 },
  directional: { color: '#ffffff', intensity: 1, position: [5, 8, 5], castShadow: true },
  point: { color: '#ffffff', intensity: 1, position: [0, 5, 0], distance: 50, decay: 2 },
  spot: { color: '#ffffff', intensity: 1, position: [0, 5, 0], target: [0, 0, 0], distance: 50, decay: 2, angle: Math.PI / 6, penumbra: 0.5 },
};

export function LightPanel() {
  const { lights, addLight, updateLight, removeLight, toggleLight } = useSceneStore();
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLightType, setNewLightType] = useState<string>('point');

  const handleAddLight = () => {
    const config = defaultLightConfigs[newLightType] || {};
    addLight({
      name: `${lightTypeLabels[newLightType]} ${lights.length + 1}`,
      type: newLightType as any,
      enabled: true,
      ...config,
    } as any);
    setShowAddModal(false);
  };

  const updatePosition = (lightId: string, axis: 'x' | 'y' | 'z', value: number) => {
    const light = lights.find((l) => l.id === lightId);
    if (!light) return;
    const pos = light.position || [0, 0, 0];
    const newPost = { x: pos[0], y: pos[1], z: pos[2] };
    newPost[axis] = value;
    updateLight(lightId, { position: [newPost.x, newPost.y, newPost.z] });
  };

  const updateTarget = (lightId: string, axis: 'x' | 'y' | 'z', value: number) => {
    const light = lights.find((l) => l.id === lightId);
    if (!light) return;
    const t = light.target || [0, 0, 0];
    const newTarget = { x: t[0], y: t[1], z: t[2] };
    newTarget[axis] = value;
    updateLight(lightId, { target: [newTarget.x, newTarget.y, newTarget.z] });
  };

  return (
    <div style={{ padding: '12px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Text strong style={{ fontSize: 13 }}>灯光管理</Text>
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setShowAddModal(true)}
          style={{ fontSize: 11 }}
        >
          添加
        </Button>
      </div>

      <Divider style={{ margin: '8px 0' }} />

      {lights.length === 0 ? (
        <Text style={{ fontSize: 12, color: '#666' }}>暂无灯光，点击上方添加</Text>
      ) : (
        <Collapse
          ghost
          size="small"
          defaultActiveKey={lights.map((l) => l.id)}
          items={lights.map((light) => ({
            key: light.id,
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                {lightTypeIcons[light.type]}
                <Text style={{ fontSize: 12, flex: 1 }}>{light.name}</Text>
                {/* 2026-10-15（P0-⑤ §8.2）：选中灯光 → 场景 gizmo 高亮（与场景树联动） */}
                <Button
                  type="text"
                  size="small"
                  data-testid={`light-select-${light.id}`}
                  icon={<AimOutlined />}
                  onClick={(e) => { e.stopPropagation(); useSelectionStore.getState().selectLight(light.id); }}
                  style={{ color: useSelectionStore.getState().lightSelectedId === light.id ? '#faad14' : '#888' }}
                />
                <Button
                  type="text"
                  size="small"
                  icon={light.enabled ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                  onClick={(e) => { e.stopPropagation(); toggleLight(light.id); }}
                  style={{ color: light.enabled ? '#1890ff' : '#666' }}
                />
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={(e) => { e.stopPropagation(); removeLight(light.id); }}
                />
              </div>
            ),
            children: (
              <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>名称</Text>
                  <Input
                    size="small"
                    value={light.name}
                    onChange={(e) => updateLight(light.id, { name: e.target.value })}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text style={{ fontSize: 11, color: '#888' }}>启用</Text>
                  <Switch
                    size="small"
                    checked={light.enabled}
                    onChange={() => toggleLight(light.id)}
                  />
                </div>

                <div>
                  <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>颜色</Text>
                  <ColorPicker
                    size="small"
                    value={light.color}
                    onChange={(c) => updateLight(light.id, { color: c.toHexString() })}
                    style={{ width: '100%' }}
                  />
                </div>

                <div>
                  <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>
                    强度: {light.intensity.toFixed(2)}
                  </Text>
                  <input
                    type="range"
                    min="0"
                    max="5"
                    step="0.1"
                    value={light.intensity}
                    onChange={(e) => updateLight(light.id, { intensity: parseFloat(e.target.value) })}
                    style={{ width: '100%' }}
                  />
                </div>

                {light.position && (
                  <div>
                    <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>位置</Text>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {(['x', 'y', 'z'] as const).map((axis) => (
                        <InputNumber
                          key={axis}
                          size="small"
                          style={{ flex: 1 }}
                          value={light.position![['x', 'y', 'z'].indexOf(axis)]}
                          onChange={(v) => updatePosition(light.id, axis, v || 0)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {light.target && (
                  <div>
                    <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>目标</Text>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {(['x', 'y', 'z'] as const).map((axis) => (
                        <InputNumber
                          key={axis}
                          size="small"
                          style={{ flex: 1 }}
                          value={light.target![['x', 'y', 'z'].indexOf(axis)]}
                          onChange={(v) => updateTarget(light.id, axis, v || 0)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {light.type === 'point' && (
                  <>
                    <div>
                      <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>
                        距离: {light.distance ?? 50}
                      </Text>
                      <input
                        type="range"
                        min="0"
                        max="200"
                        step="1"
                        value={light.distance ?? 50}
                        onChange={(e) => updateLight(light.id, { distance: parseFloat(e.target.value) })}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>
                        衰减: {light.decay ?? 2}
                      </Text>
                      <input
                        type="range"
                        min="0"
                        max="5"
                        step="0.1"
                        value={light.decay ?? 2}
                        onChange={(e) => updateLight(light.id, { decay: parseFloat(e.target.value) })}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </>
                )}

                {light.type === 'spot' && (
                  <>
                    <div>
                      <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>
                        角度: {((light.angle ?? Math.PI / 6) * 180 / Math.PI).toFixed(0)}°
                      </Text>
                      <input
                        type="range"
                        min="5"
                        max="90"
                        step="1"
                        value={(light.angle ?? Math.PI / 6) * 180 / Math.PI}
                        onChange={(e) => updateLight(light.id, { angle: parseFloat(e.target.value) * Math.PI / 180 })}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>
                        半影: {(light.penumbra ?? 0.5).toFixed(2)}
                      </Text>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={light.penumbra ?? 0.5}
                        onChange={(e) => updateLight(light.id, { penumbra: parseFloat(e.target.value) })}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>
                        距离: {light.distance ?? 50}
                      </Text>
                      <input
                        type="range"
                        min="0"
                        max="200"
                        step="1"
                        value={light.distance ?? 50}
                        onChange={(e) => updateLight(light.id, { distance: parseFloat(e.target.value) })}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </>
                )}

                {(light.type === 'directional' || light.type === 'spot') && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ fontSize: 11, color: '#888' }}>投射阴影</Text>
                    <Switch
                      size="small"
                      checked={light.castShadow ?? false}
                      onChange={(v) => updateLight(light.id, { castShadow: v })}
                    />
                  </div>
                )}
              </Space>
            ),
          }))}
        />
      )}

      <Modal
        title="添加灯光"
        open={showAddModal}
        onOk={handleAddLight}
        onCancel={() => setShowAddModal(false)}
        okText="添加"
        cancelText="取消"
        width={320}
      >
        <Space orientation="vertical" style={{ width: '100%' }}>
          <Text style={{ fontSize: 12 }}>选择灯光类型</Text>
          <Select
            style={{ width: '100%' }}
            value={newLightType}
            onChange={setNewLightType}
            options={[
              { value: 'ambient', label: '环境光 - 均匀照亮整个场景' },
              { value: 'directional', label: '平行光 - 模拟太阳光' },
              { value: 'point', label: '点光源 - 向四周发射光线' },
              { value: 'spot', label: '聚光灯 - 锥形光束' },
            ]}
          />
        </Space>
      </Modal>
    </div>
  );
}

export default LightPanel;
